/**
 * Weekly View Component
 * Shows hours logged for each day of the week (Monday-Sunday)
 */

import React, { useMemo } from 'react';
import { Card, Tooltip, Spin } from 'antd';
import { useDailySummaries } from '@/hooks/useTimesheet';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import './WeeklyView.css';

dayjs.extend(isoWeek);

interface WeeklyViewProps {
  year: number;
  week: number; // ISO week number (1-53)
  showAll: boolean;
  canEdit: boolean;
}

export const WeeklyView: React.FC<WeeklyViewProps> = ({
  year,
  week,
  showAll,
  canEdit: _canEdit,
}) => {
  // Calculate date range for the week
  const { startDate, endDate, weekDays } = useMemo(() => {
    const start = dayjs().year(year).isoWeek(week).startOf('isoWeek');
    const end = start.endOf('isoWeek');
    const days = Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));

    return {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      weekDays: days,
    };
  }, [year, week]);

  const { data, isLoading } = useDailySummaries({
    date_from: startDate,
    date_to: endDate,
  });

  // Group summaries by date
  const summariesByDate = useMemo(() => {
    const summaries = data?.items || [];
    const grouped: Record<string, typeof summaries> = {};
    summaries.forEach((summary) => {
      if (!grouped[summary.summary_date]) {
        grouped[summary.summary_date] = [];
      }
      grouped[summary.summary_date].push(summary);
    });
    return grouped;
  }, [data?.items]);

  // Calculate hours for each day
  const dayHours = useMemo(() => {
    return weekDays.map((day) => {
      const dateStr = day.format('YYYY-MM-DD');
      const daySummaries = summariesByDate[dateStr] || [];

      const hours = daySummaries.reduce((sum, s) => {
        return sum + (showAll ? s.total_hours_logged : s.approved_hours_logged);
      }, 0);

      const billable = daySummaries.reduce(
        (sum, s) => sum + s.billable_hours_logged,
        0
      );

      const nonBillable = daySummaries.reduce(
        (sum, s) => sum + s.non_billable_hours_logged,
        0
      );

      const projects = daySummaries.length;

      return {
        date: dateStr,
        day: day.format('ddd'),
        dayNumber: day.date(),
        hours,
        billable,
        nonBillable,
        projects,
        isToday: day.isSame(dayjs(), 'day'),
        isWeekend: day.day() === 0 || day.day() === 6,
      };
    });
  }, [weekDays, summariesByDate, showAll]);

  // Calculate totals
  const totals = dayHours.reduce(
    (acc, day) => ({
      hours: acc.hours + day.hours,
      billable: acc.billable + day.billable,
      nonBillable: acc.nonBillable + day.nonBillable,
    }),
    { hours: 0, billable: 0, nonBillable: 0 }
  );

  if (isLoading) {
    return (
      <div className="timesheet-weekly-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="timesheet-weekly-view">
      {/* Header with week info */}
      <div className="timesheet-weekly-header">
        <h3 className="timesheet-weekly-title">
          Week {week} of {year}
        </h3>
        <div className="timesheet-weekly-range">
          {weekDays[0].format('MMM D')} - {weekDays[6].format('MMM D, YYYY')}
        </div>
      </div>

      {/* Totals Summary */}
      <div className="timesheet-weekly-summary">
        <Card className="timesheet-weekly-summary-card">
          <div className="timesheet-weekly-summary-item">
            <span className="timesheet-weekly-summary-label">Total Hours</span>
            <span className="timesheet-weekly-summary-value">
              {totals.hours.toFixed(2)}h
            </span>
          </div>
          <div className="timesheet-weekly-summary-item">
            <span className="timesheet-weekly-summary-label">Billable</span>
            <span
              className="timesheet-weekly-summary-value"
              style={{ color: 'var(--timesheet-billable)' }}
            >
              {totals.billable.toFixed(2)}h
            </span>
          </div>
          <div className="timesheet-weekly-summary-item">
            <span className="timesheet-weekly-summary-label">Non-Billable</span>
            <span
              className="timesheet-weekly-summary-value"
              style={{ color: 'var(--timesheet-non-billable)' }}
            >
              {totals.nonBillable.toFixed(2)}h
            </span>
          </div>
          <div className="timesheet-weekly-summary-item">
            <span className="timesheet-weekly-summary-label">Avg per Day</span>
            <span className="timesheet-weekly-summary-value">
              {(totals.hours / 7).toFixed(2)}h
            </span>
          </div>
        </Card>
      </div>

      {/* Days Grid */}
      <div className="timesheet-weekly-grid">
        {dayHours.map((dayData) => {
          const hasData = dayData.hours > 0;

          return (
            <Card
              key={dayData.date}
              className={`timesheet-weekly-card ${
                dayData.isToday ? 'timesheet-weekly-card-today' : ''
              } ${dayData.isWeekend ? 'timesheet-weekly-card-weekend' : ''} ${
                hasData ? 'timesheet-weekly-card-has-data' : ''
              }`}
            >
              {/* Day Header */}
              <div className="timesheet-weekly-card-header">
                <span className="timesheet-weekly-day-name">{dayData.day}</span>
                <span className="timesheet-weekly-day-number">
                  {dayData.dayNumber}
                </span>
              </div>

              {/* Hours Content */}
              {hasData ? (
                <Tooltip
                  title={
                    <div>
                      <div>Total: {dayData.hours.toFixed(2)}h</div>
                      <div>Billable: {dayData.billable.toFixed(2)}h</div>
                      <div>Non-Billable: {dayData.nonBillable.toFixed(2)}h</div>
                      <div>Projects: {dayData.projects}</div>
                    </div>
                  }
                >
                  <div className="timesheet-weekly-card-content">
                    <div className="timesheet-weekly-card-hours">
                      {dayData.hours.toFixed(1)}
                      <span className="timesheet-weekly-card-unit">h</span>
                    </div>
                    <div className="timesheet-weekly-card-breakdown">
                      {dayData.billable > 0 && (
                        <div
                          className="timesheet-weekly-card-bar"
                          style={{
                            width: `${(dayData.billable / dayData.hours) * 100}%`,
                            background: 'var(--timesheet-billable)',
                          }}
                        />
                      )}
                      {dayData.nonBillable > 0 && (
                        <div
                          className="timesheet-weekly-card-bar"
                          style={{
                            width: `${(dayData.nonBillable / dayData.hours) * 100}%`,
                            background: 'var(--timesheet-non-billable)',
                          }}
                        />
                      )}
                    </div>
                    <div className="timesheet-weekly-card-projects">
                      {dayData.projects}{' '}
                      {dayData.projects === 1 ? 'project' : 'projects'}
                    </div>
                  </div>
                </Tooltip>
              ) : (
                <div className="timesheet-weekly-card-empty">No entries</div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
